/**
 * AtlasFireworksModule — FIREWORKS and LIGHT SHOWS (TOWN LIFE, 2026-09-24).
 *
 * Sources (all ARITHMETIC every client agrees on — the only rows are the players' own launches):
 *   PLAYERS    `firework_launch` rows (a rocket of a kind and palette, or a whole show from its seed), drawn from
 *              the INSERT at the launch spot; a row older than 20 s when it arrives is history, never replayed.
 *   TOWNS      every city and capital puts on a night show: a 60 s show every SHOW_PERIOD_S, its offset hashed from
 *              the site, only while the sky is dark — searchlights sweep the sky and shells break over the square.
 *   FESTIVALS  a live `festival` / `parade` living event (and a feast after dark) gets a short show over its venue
 *              every ~90 s while it runs.
 *   RACES      the Grand Circuit's finish (`atlas:firework-show` from the racing module) — a show over the line.
 * THE SPARKS are ONE instanced quad batch (per-instance centre/size + colour/alpha, additive, markMrtOverlay):
 *   peony, willow (drooping gold trails), ring (a tilted halo), crossette (comets that split), palm (thick
 *   comets that fall like fronds), strobe (blinking). A rocket climbs on a spark trail, then breaks.
 * THE LIGHT: the break's flash is a FLASH on the shared light broker (fire realism R2, 2026-09-25 — the fire
 * module's session-fixed pool; THE LIGHT-SET IDENTITY LAW lives there): it flashes the ground under the brightest
 * recent break; searchlights are additive cones with a fading gradient.
 * FIREWORKS REALISM (R8, 2026-09-25):
 *   · SMOKE PER BREAK — drifting shell smoke is THE realism tell of a real show: every break leaves 4–7 puffs that
 *     drift with the wind and linger 10–16 s, LIT BY THE NEXT BREAKS (the flash's position, colour and decay feed the
 *     puff batch's light uniforms; each puff is lit by its distance to the flash) — one instanced batch, both backends.
 *   · VELOCITY-STRETCHED SPARKS — a spark's quad turns along its SCREEN velocity and stretches with it (a streak,
 *     never a round dot), like the GPU particles' 'stretched' mode.
 *   · A MIRRORED REFLECTION across the water plane below ultra (ultra has the planar reflector) when a break lies
 *     over the sea or a lake: the same sparks, flipped about the waterline, dimmed.
 *   · CRACKLE MICRO-STARS — strobe / crossette / willow shells throw a glitter of micro-stars that each flash once over
 *     the ~2 s the crackle clip plays (seen before it is heard: the light arrives at once, the sound at d / 343 m/s).
 * Levers: `__fireworks()` (live sparks, shows, sources, smoke, reflections), `__fireworks().test(kind)`.
 */
import * as THREE from 'three/webgpu';
import { instancedDynamicBufferAttribute, texture, vec2, vec3, vec4, float, Fn, cameraViewMatrix, atan, length, uniform, positionPrevious } from 'three/tsl';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { groundYIn } from '../lib/grounding.js';
import { sfxAt, sfxAvailable } from '../lib/sfx.js';
import { settlementSites } from '../../../shared/settlements.js';
import { FIREWORK_PALETTES, FIREWORK_KINDS, type FireworkKind } from '../lib/town_life.js';
import { commonsSeedNow } from '../lib/town_names.js';
import { uNightFactor } from './SkyModule.js';
import { fireSystem } from '../lib/vfx/fire_system.js';
import { activePreset } from '../lib/quality_tiers.js';
import { windVector } from '../lib/wind.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { bakedLakesNear } from '../lib/lakes.js';
// surfaces (2026-09-27): §4.3 the stars and the break's light on the physical curve
import { EMITTER_NITS, EMITTER_DISPLAY_MAX, FIREWORK_BREAK_CANDELA, emitterScene } from '../lib/vfx/fire_rules.js';
import { exposureState, lightFrame } from '../lib/photometry.js';
/** §4.3: the stars' scale — emitterScene(5 kcd/m², held at display 1.2) over the palette's unit colour (1 = the pre-curve units). */
const uSparkEmission = uniform(1);

const CAPACITY = 6000;
const VIEW_M = 3500;
const GRAVITY = 9.8;
const SHOW_PERIOD_S = 45 * 60;
const TOWN_SHOW_S = 60;
const FESTIVAL_EVERY_S = 90;
const SPEED_OF_SOUND = 343;

/** A seeded 0..1 stream (mulberry32). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13;
  return h >>> 0;
}

interface Spark {
  x: number; y: number; z: number; vx: number; vy: number; vz: number;
  age: number; life: number; size: number; drag: number; grav: number;
  r: number; g: number; b: number; strobe: boolean; trail: number; trailT: number;
  /** A comet splits (crossette) at this age into 4; 0 = never. */
  split: number; kind: FireworkKind | 'rocket' | 'trail'; palette: number; burstH: number;
  /** A rocket's shell (what breaks at the top). */
  shell?: FireworkKind;
  /** The water plane under the break (NaN = over land): the mirrored reflection is drawn about it. */
  waterY?: number;
  /** A crackle micro-star: invisible until `flashAt`, then one brief flash. */
  flashAt?: number;
}

interface Launch { at: number; x: number; y: number; z: number; kind: FireworkKind; palette: number; height: number; big: boolean }

/** The spark batch: like fx_sprite_batch but colour and alpha are rewritten every frame (sparks fade). */
class SparkBatch {
  readonly sprite: THREE.Sprite;
  private readonly cap: number;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly vel: Float32Array;
  private readonly posAttr: THREE.InstancedBufferAttribute;
  private readonly colAttr: THREE.InstancedBufferAttribute;
  private readonly velAttr: THREE.InstancedBufferAttribute;
  private written = 0;
  /**
   * `mirror` = the REFLECTION batch (R8): it only takes sparks whose break lies over water and writes them flipped
   * about that water plane, dimmed. Both batches stretch every quad along its SCREEN velocity: the quad turns so its
   * +Y follows the velocity projected into view space and grows by |v| × 50 ms along it (a streak, not a dot).
   */
  constructor(map: THREE.Texture, cap: number, private readonly mirror = false) {
    this.cap = cap;
    this.pos = new Float32Array(cap * 4); this.col = new Float32Array(cap * 4); this.vel = new Float32Array(cap * 4);
    this.posAttr = new THREE.InstancedBufferAttribute(this.pos, 4);
    this.colAttr = new THREE.InstancedBufferAttribute(this.col, 4);
    this.velAttr = new THREE.InstancedBufferAttribute(this.vel, 4);
    for (const a of [this.posAttr, this.colAttr, this.velAttr]) a.setUsage(THREE.DynamicDrawUsage);
    const aPos = instancedDynamicBufferAttribute(this.posAttr, 'vec4');
    const aCol = instancedDynamicBufferAttribute(this.colAttr, 'vec4');
    const aVel = instancedDynamicBufferAttribute(this.velAttr, 'vec4');
    const mat = new THREE.SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    // THE positionPrevious LAW: three's default previous position is the raw quad corner, so every spark would carry a
    // "velocity" equal to its distance from the origin under motion blur / TAAU. Pinned to the current centre (zero
    // object velocity — the streak is drawn by the scaleNode; the camera's own motion still blurs it).
    mat.positionNode = Fn(() => { positionPrevious.assign(aPos.xyz); return aPos.xyz; })();
    // The screen velocity: into view space (w = 0: a direction); the quad's +Y turns onto it.
    const vView = cameraViewMatrix.mul(vec4(aVel.xyz, 0)).xyz;
    const vs = length(vView.xy);
    mat.rotationNode = atan(vView.y, vView.x).sub(Math.PI / 2);
    mat.scaleNode = vec2(aPos.w, aPos.w.add(vs.mul(aVel.w)));
    mat.colorNode = texture(map).mul(vec4(aCol.xyz.mul(uSparkEmission), aCol.w)); // §4.3 (surfaces, 2026-09-27): on the curve
    mat.fog = false; // a firework is brighter than the haze it bursts in
    mat.name = mirror ? 'fireworks-reflection' : 'fireworks';
    markMrtOverlay(mat);
    const sp = new THREE.Sprite(mat as unknown as THREE.SpriteMaterial);
    sp.count = cap; // FIXED (the cache-key law); dead slots are zero-size quads
    sp.frustumCulled = false;
    sp.visible = false;
    sp.renderOrder = mirror ? 2 : 3;
    sp.name = mirror ? 'firework-reflection' : 'firework-sparks';
    this.sprite = sp;
  }
  /** Write the live sparks (anchor = the sprite's own position, which follows the viewer for sort depth). */
  write(sparks: Spark[], ax: number, ay: number, az: number, t: number): number {
    const p = this.pos, c = this.col, v = this.vel;
    let n = 0;
    for (let i = 0; i < sparks.length && n < this.cap; i++) {
      const s = sparks[i];
      if (this.mirror && !(s.waterY !== undefined && Number.isFinite(s.waterY) && s.y > s.waterY)) continue;
      const o = n * 4;
      const u = s.age / s.life;
      let a = u < 0.08 ? u / 0.08 : 1 - Math.pow(Math.max(0, (u - 0.35) / 0.65), 1.4);
      if (s.strobe && u > 0.3) a *= (Math.floor(s.age * 14 + s.size * 97) % 2 === 0) ? 1 : 0.08;
      // A crackle micro-star: dark until its moment, then one 60 ms flash.
      if (s.flashAt !== undefined) { const d = s.age - s.flashAt; a = d < 0 ? 0 : d < 0.06 ? 1 - d / 0.06 : 0; }
      const y = this.mirror ? 2 * (s.waterY as number) - s.y : s.y;
      p[o] = s.x - ax; p[o + 1] = y - ay; p[o + 2] = s.z - az; p[o + 3] = s.size * (s.kind === 'trail' ? 1 - u * 0.7 : 1);
      v[o] = s.vx; v[o + 1] = this.mirror ? -s.vy : s.vy; v[o + 2] = s.vz; v[o + 3] = s.kind === 'rocket' ? 0.03 : 0.05;
      // A spark cools as it falls: toward a deeper, redder tone.
      const cool = u * u * 0.55;
      const k = this.mirror ? 0.32 : 1; // the reflection: the sea's Fresnel at a grazing view, roughly a third
      c[o] = s.r * (1 - cool * 0.2) * k; c[o + 1] = s.g * (1 - cool) * k; c[o + 2] = s.b * (1 - cool * 1.3) * k; c[o + 3] = Math.max(0, a);
      n++;
    }
    for (let i = n; i < this.written; i++) p[i * 4 + 3] = 0;
    // PERF (shader sweep C, 2026-10-06; THE UPLOAD-RANGE LAW): a bare needsUpdate sent the whole capacity of all three
    // buffers every frame of a show (and the mirror batch's too); only the live prefix and the slots just freed moved.
    const hi = Math.max(n, this.written);
    if (hi > 0) for (const a of [this.posAttr, this.colAttr, this.velAttr]) { a.clearUpdateRanges(); a.addUpdateRange(0, hi * 4); a.needsUpdate = true; }
    this.written = n;
    this.sprite.visible = n > 0;
    void t;
    return n;
  }
}

/**
 * THE SHELL SMOKE (R8): one instanced puff batch (fixed count — the cache-key law), premultiplied OVER on both
 * backends, each puff LIT BY THE FLASH of the latest break by its own distance to it (1 / (1 + (d / 60 m)²)) over a
 * dim night-sky ambient — so the smoke of break n is lit up by break n + 1.
 */
const SMOKE_CAP = 320;
interface Puff { x: number; y: number; z: number; vx: number; vy: number; vz: number; age: number; life: number; size: number; a: number }
class SmokeBatch {
  readonly sprite: THREE.Sprite;
  private readonly pos = new Float32Array(SMOKE_CAP * 4);
  private readonly prm = new Float32Array(SMOKE_CAP * 4);
  private readonly posAttr: THREE.InstancedBufferAttribute;
  private readonly prmAttr: THREE.InstancedBufferAttribute;
  readonly uFlashPos = uniform(new THREE.Vector3(0, -1e6, 0));
  readonly uFlashCol = uniform(new THREE.Vector3(0, 0, 0));
  readonly uAmbient = uniform(new THREE.Vector3(0.02, 0.022, 0.03));
  private written = 0;
  constructor(map: THREE.Texture) {
    this.posAttr = new THREE.InstancedBufferAttribute(this.pos, 4);
    this.prmAttr = new THREE.InstancedBufferAttribute(this.prm, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage); this.prmAttr.setUsage(THREE.DynamicDrawUsage);
    const aPos = instancedDynamicBufferAttribute(this.posAttr, 'vec4');
    const aPrm = instancedDynamicBufferAttribute(this.prmAttr, 'vec4');
    const mat = new THREE.SpriteNodeMaterial({ transparent: true, depthWrite: false });
    mat.blending = THREE.CustomBlending;
    mat.blendSrc = THREE.OneFactor; mat.blendDst = THREE.OneMinusSrcAlphaFactor;
    mat.blendSrcAlpha = THREE.OneFactor; mat.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    mat.positionNode = Fn(() => { positionPrevious.assign(aPos.xyz); return aPos.xyz; })(); // the positionPrevious law (above)
    mat.scaleNode = vec2(aPos.w, aPos.w);
    mat.rotationNode = aPrm.y;
    mat.colorNode = Fn(() => {
      const tex = texture(map);
      // aPrm.z = the puff's distance to the flash (CPU, per frame) → the light it receives.
      const lit = this.uAmbient.add(this.uFlashCol.mul(float(1).div(float(1).add(aPrm.z.div(60).pow(2)))));
      const a = tex.w.mul(aPrm.x);
      return vec4(vec3(0.55, 0.53, 0.5).mul(lit).mul(a), a);
    })();
    mat.fog = false;
    mat.name = 'fireworks-smoke';
    markMrtOverlay(mat);
    const sp = new THREE.Sprite(mat as unknown as THREE.SpriteMaterial);
    sp.count = SMOKE_CAP;
    sp.frustumCulled = false;
    sp.visible = false;
    sp.renderOrder = 1;
    sp.name = 'firework-smoke';
    this.sprite = sp;
  }
  write(puffs: Puff[], ax: number, ay: number, az: number, fx: number, fy: number, fz: number): number {
    const n = Math.min(SMOKE_CAP, puffs.length);
    for (let i = 0; i < n; i++) {
      const q = puffs[i], o = i * 4;
      const u = q.age / q.life;
      const a = q.a * Math.min(1, u / 0.06) * (1 - u) ** 1.2;
      this.pos[o] = q.x - ax; this.pos[o + 1] = q.y - ay; this.pos[o + 2] = q.z - az; this.pos[o + 3] = q.size * (0.45 + 0.9 * Math.sqrt(u));
      this.prm[o] = a; this.prm[o + 1] = (i * 2.399) % 6.283; this.prm[o + 2] = Math.hypot(q.x - fx, q.y - fy, q.z - fz); this.prm[o + 3] = 0;
    }
    for (let i = n; i < this.written; i++) this.pos[i * 4 + 3] = 0;
    const hi = Math.max(n, this.written); // the live prefix only (THE UPLOAD-RANGE LAW — as the sparks)
    if (hi > 0) for (const a of [this.posAttr, this.prmAttr]) { a.clearUpdateRanges(); a.addUpdateRange(0, hi * 4); a.needsUpdate = true; }
    this.written = n;
    this.sprite.visible = n > 0;
    return n;
  }
}

function sparkTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 32;
  const g = c.getContext('2d')!;
  const grd = g.createRadialGradient(16, 16, 0, 16, 16, 16);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.18, 'rgba(255,255,255,0.9)');
  grd.addColorStop(0.45, 'rgba(255,255,255,0.3)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 32, 32);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
/** A soft, lumpy puff (the shell smoke): a few overlapping radial blobs, deterministic. */
function puffTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  let seed = 23;
  const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  for (let i = 0; i < 9; i++) {
    const x = 32 + (rnd() - 0.5) * 22, y = 32 + (rnd() - 0.5) * 22, r = 12 + rnd() * 14;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, 'rgba(255,255,255,0.32)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 64, 64);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}
function beamTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = 8; c.height = 128;
  const g = c.getContext('2d')!;
  const grd = g.createLinearGradient(0, 128, 0, 0); // the cone's v runs 0 at its base → 1 at the tip
  grd.addColorStop(0, 'rgba(255,255,255,0.55)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.18)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 8, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

interface Show { key: string; x: number; y: number; z: number; ends: number; beams: THREE.Mesh[]; palette: number; seed: number }

export class AtlasFireworksModule implements IModule {
  readonly name = 'AtlasFireworks';
  private scene!: THREE.Scene;
  private batch: SparkBatch | null = null;
  private mirror: SparkBatch | null = null;
  private smoke: SmokeBatch | null = null;
  private puffs: Puff[] = [];
  private sparks: Spark[] = [];
  private launches: Launch[] = [];
  private shows = new Map<string, Show>();
  /** The latest break's flash (the broker holds the light; this is what the shell smoke is lit by). */
  private flashAt = new THREE.Vector3(0, -1e6, 0);
  private flashColor = new THREE.Color();
  private flashLeft = 0;
  private flashPeak = 0;
  private beamGeo: THREE.CylinderGeometry | null = null;
  private beamTex: THREE.Texture | null = null;
  private seen = new Set<string>();
  private townShowAt = 0;
  private festivalAt = new Map<string, number>();
  private last = performance.now();
  private subbed = false;
  private sitesSeed = '';
  private bigSites: ReturnType<typeof settlementSites> = [];
  private stats = { launched: 0, bursts: 0, rows: 0, townShows: 0, festivalShows: 0, raceShows: 0, puffs: 0, reflected: 0, microStars: 0, flashes: 0 };

  init(app: App): void {
    this.scene = app.world.scene;
    const tex = sparkTexture();
    this.batch = new SparkBatch(tex, CAPACITY);
    this.scene.add(this.batch.sprite);
    // The reflection batch (R8): drawn below ultra (ultra carries the planar reflector); half the capacity.
    this.mirror = new SparkBatch(tex, CAPACITY / 2, true);
    this.scene.add(this.mirror.sprite);
    this.smoke = new SmokeBatch(puffTexture());
    this.scene.add(this.smoke.sprite);
    // No light of its own any more: the break's flash is a FLASH on the fire module's shared broker.
    app.world.addSystem(makeSystem('Fireworks', Phase.RenderSub, 26, () => this.tick()));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:firework-show', (p) => { this.stats.raceShows++; this.startShow(`bus:${p.seed}:${Math.round(p.x)}`, p.x, p.z, p.seed, 32, true); });
    bus.on('atlas:world-cleared', () => this.clearAll());
    (window as unknown as Record<string, unknown>).__fireworks = () => ({
      live: this.sparks.length, pending: this.launches.length, shows: [...this.shows.keys()], smoke: this.puffs.length, ...this.stats,
      test: (kind: FireworkKind = 'peony', palette = 0) => this.schedule({ at: performance.now() / 1000, x: atlas.localX - Math.sin(atlas.localYaw) * 40, y: this.ground(atlas.localX, atlas.localZ), z: atlas.localZ - Math.cos(atlas.localYaw) * 40, kind, palette, height: 70, big: true }),
      show: (seed = 7) => this.startShow(`test:${seed}`, atlas.localX - Math.sin(atlas.localYaw) * 60, atlas.localZ - Math.cos(atlas.localYaw) * 60, seed, 30, true),
      freeze: (on = true) => { this.frozen = !!on; return this.frozen; },
    });
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM firework_launch']); } catch { /* reconnect */ }
    const t = (conn.db as unknown as { fireworkLaunch?: { onInsert(cb: (c: unknown, r: { launchId: bigint; worldId: bigint; x: number; y: number; z: number; kind: string; palette: number; seed: number; at: { microsSinceUnixEpoch: bigint } }) => void): void } }).fireworkLaunch;
    t?.onInsert((_c, r) => {
      const key = r.launchId.toString();
      if (this.seen.has(key)) return;
      this.seen.add(key);
      if (this.seen.size > 400) this.seen = new Set([...this.seen].slice(-200));
      const age = Date.now() - Number(r.at.microsSinceUnixEpoch / 1000n);
      if (age > 20_000 || r.worldId !== atlas.currentWorldId) return; // history on subscribe, or another world
      this.stats.rows++;
      if (r.kind === 'show') { this.startShow(`row:${key}`, r.x, r.z, r.seed, 40, true); return; }
      const kind = (FIREWORK_KINDS as readonly string[]).includes(r.kind) ? r.kind as FireworkKind : 'peony';
      this.schedule({ at: performance.now() / 1000, x: r.x, y: this.ground(r.x, r.z), z: r.z, kind, palette: r.palette % FIREWORK_PALETTES.length, height: 70 + (r.seed % 30), big: true });
    });
  }

  private ground(x: number, z: number): number { return atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0; }

  // ── Shows ───────────────────────────────────────────────────────────────────
  /** A choreographed show: `durS` of shells from a seed (a finale in the last sixth), with searchlights. */
  private startShow(key: string, x: number, z: number, seed: number, durS: number, beams: boolean): void {
    if (this.shows.has(key)) return;
    if (Math.hypot(x - atlas.localX, z - atlas.localZ) > VIEW_M) return;
    const now = performance.now() / 1000;
    const y = this.ground(x, z);
    const r = rng(seed * 2654435761 + 17);
    const palette = Math.floor(r() * FIREWORK_PALETTES.length);
    const n = Math.round(durS * 0.7);
    for (let i = 0; i < n; i++) {
      const t = (i / n) * durS * 0.82 + r() * 0.9;
      this.schedule({ at: now + t, x: x + (r() - 0.5) * 60, y, z: z + (r() - 0.5) * 60, kind: FIREWORK_KINDS[Math.floor(r() * FIREWORK_KINDS.length)], palette: r() < 0.6 ? palette : Math.floor(r() * FIREWORK_PALETTES.length), height: 65 + r() * 70, big: r() < 0.3 });
    }
    for (let i = 0; i < 12; i++) { // the finale
      this.schedule({ at: now + durS * 0.85 + i * 0.35 + r() * 0.2, x: x + (r() - 0.5) * 80, y, z: z + (r() - 0.5) * 80, kind: i % 3 === 0 ? 'willow' : i % 3 === 1 ? 'peony' : 'crossette', palette: Math.floor(r() * FIREWORK_PALETTES.length), height: 90 + r() * 50, big: true });
    }
    const show: Show = { key, x, y, z, ends: now + durS + 4, beams: [], palette, seed };
    if (beams) show.beams = this.makeBeams(x, y, z, palette, r);
    this.shows.set(key, show);
  }

  private makeBeams(x: number, y: number, z: number, palette: number, r: () => number): THREE.Mesh[] {
    if (!this.beamGeo) {
      this.beamGeo = new THREE.CylinderGeometry(9, 0.6, 420, 16, 1, true);
      this.beamGeo.translate(0, 210, 0); // the base at the origin, the mouth up
    }
    if (!this.beamTex) this.beamTex = beamTexture();
    const out: THREE.Mesh[] = [];
    for (let i = 0; i < 4; i++) {
      const pal = FIREWORK_PALETTES[(palette + i) % FIREWORK_PALETTES.length];
      const m = new THREE.MeshBasicMaterial({ color: i % 2 === 0 ? pal.b : pal.a, map: this.beamTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
      markMrtOverlay(m);
      const beam = new THREE.Mesh(this.beamGeo, m);
      const a = (i / 4) * Math.PI * 2 + r();
      beam.position.set(x + Math.cos(a) * 28, y, z + Math.sin(a) * 28);
      beam.userData.phase = r() * Math.PI * 2;
      beam.userData.speed = 0.35 + r() * 0.3;
      beam.renderOrder = 1;
      beam.name = 'lightshow-beam';
      this.scene.add(beam);
      out.push(beam);
    }
    return out;
  }

  private schedule(l: Launch): void {
    if (Math.hypot(l.x - atlas.localX, l.z - atlas.localZ) > VIEW_M) return;
    this.launches.push(l);
  }

  // ── The frame ───────────────────────────────────────────────────────────────
  /** Probe lever (fire session 3, the still-camera smear test under TAAU / the blur): hold every spark and puff. Never set in play. */
  private frozen = false;

  private tick(): void {
    const nowMs = performance.now();
    const dt = this.frozen ? 0 : Math.min(0.1, (nowMs - this.last) / 1000);
    this.last = nowMs;
    const now = nowMs / 1000;
    if (atlas.currentWorldId === null || atlas.spaceFlight) { if (this.sparks.length || this.shows.size) this.clearAll(); return; }
    // §4.3 (surfaces, 2026-09-27): a star is 5 kcd/m² (faint by day, as a real one is), held at display 1.2 at night; the
    // fire system's A/B lever (`__fire().physical(false)`) restores the pre-curve units here too.
    const onCurve = (fireSystem()?.physical ?? true) && lightFrame.valid;
    (uSparkEmission as unknown as { value: number }).value = onCurve ? emitterScene(EMITTER_NITS.spark, EMITTER_DISPLAY_MAX.spark, exposureState) : 1;
    // Frozen (the probe's still-camera test): nothing new launches either — a live show kept firing rockets into a
    // "frozen" sky (world-gpu-3, 2026-09-26).
    if (!this.frozen) { this.townShows(now); this.festivalShows(now); }
    // Launch what is due.
    for (let i = this.launches.length - 1; i >= 0 && !this.frozen; i--) {
      const l = this.launches[i];
      if (l.at > now) continue;
      this.launches.splice(i, 1);
      this.rocket(l);
    }
    this.step(dt);
    this.animateBeams(now);
    // The flash decays (the broker ages its own light; this copy lights the shell smoke).
    this.flashLeft = Math.max(0, this.flashLeft - dt);
    this.stepSmoke(dt);
    const ax = atlas.localX, ay = atlas.localY + 60, az = atlas.localZ;
    this.batch?.sprite.position.set(ax, ay, az);
    this.batch?.write(this.sparks, ax, ay, az, now);
    // The mirrored reflection only below ultra (ultra's planar reflector already mirrors the sky).
    const reflect = !activePreset().waterReflections;
    if (this.mirror) { this.mirror.sprite.position.set(ax, ay - 120, az); this.stats.reflected = reflect ? this.mirror.write(this.sparks, ax, ay - 120, az, now) : this.mirror.write([], ax, ay - 120, az, now); }
  }

  private rocket(l: Launch): void {
    this.stats.launched++;
    const c = new THREE.Color(0xffd8a0);
    const climb = 1.4 + Math.random() * 0.3;
    const vy = l.height / climb + 0.5 * GRAVITY * 0.35 * climb;
    this.push({ x: l.x, y: l.y + 1, z: l.z, vx: (Math.random() - 0.5) * 2, vy, vz: (Math.random() - 0.5) * 2, age: 0, life: climb, size: 0.9, drag: 0, grav: 0.35, r: c.r, g: c.g, b: c.b, strobe: false, trail: 0.018, trailT: 0, split: 0, kind: 'rocket', palette: l.palette, burstH: l.big ? 1.3 : 1, shell: l.kind });
    const d = Math.hypot(l.x - atlas.localX, l.z - atlas.localZ);
    if (d < 900) sfxAt(sfxAvailable('sfx_firework_launch') ? 'sfx_firework_launch' : 'sfx_blaster_fire', { x: l.x, y: l.y + 2, z: l.z }, { gain: 0.25 * Math.max(0.15, 1 - d / 900), rate: sfxAvailable('sfx_firework_launch') ? 1 : 0.55, cooldownMs: 40, occlude: false });
  }

  private push(s: Spark): void { if (this.sparks.length < CAPACITY) this.sparks.push(s); }

  /** A shell breaks at (x, y, z): the kind decides the pattern. */
  /** The water plane under (x, z): the sea, a baked lake, or NaN (land). */
  private waterUnder(x: number, z: number): number {
    const seed = commonsSeedNow();
    if (!seed || atlas.currentWorldId !== atlas.commonsWorldId) return NaN;
    const g = this.ground(x, z);
    const sea = commonsSeaLevel(seed);
    if (g < sea - 0.5) return sea;
    for (const l of bakedLakesNear(seed, x, z, 1)) if (g < l.level - 0.3 && x >= l.x0 && x <= l.x1 && z >= l.z0 && z <= l.z1) return l.level;
    return NaN;
  }

  private burst(x: number, y: number, z: number, kind: FireworkKind, palette: number, scale: number): void {
    this.stats.bursts++;
    const waterY = this.waterUnder(x, z);
    const pal = FIREWORK_PALETTES[palette] ?? FIREWORK_PALETTES[0];
    const a = new THREE.Color(pal.a), b = new THREE.Color(pal.b);
    const pick = (): THREE.Color => (Math.random() < 0.7 ? a : b);
    const sphere = (): [number, number, number] => { const u = Math.random() * 2 - 1, t = Math.random() * Math.PI * 2, s = Math.sqrt(1 - u * u); return [s * Math.cos(t), u, s * Math.sin(t)]; };
    const add = (n: number, speed: number, life: number, size: number, drag: number, grav: number, extra: Partial<Spark> = {}, dir?: () => [number, number, number]): void => {
      for (let i = 0; i < n; i++) {
        const [dx, dy, dz] = (dir ?? sphere)();
        const v = speed * (0.85 + Math.random() * 0.3) * scale;
        const c = pick();
        this.push({ x, y, z, vx: dx * v, vy: dy * v, vz: dz * v, age: 0, life: life * (0.85 + Math.random() * 0.3), size, drag, grav, r: c.r, g: c.g, b: c.b, strobe: false, trail: 0, trailT: 0, split: 0, kind, palette, burstH: 1, waterY, ...extra });
      }
    };
    switch (kind) {
      case 'peony': add(130, 17, 1.9, 1.3, 1.6, 0.35); break;
      case 'willow': add(90, 12, 3.6, 1.1, 0.7, 0.55, { trail: 0.05, r: 1, g: 0.78, b: 0.36 }); break;
      case 'ring': {
        const nx = Math.random() - 0.5, nz = Math.random() - 0.5;
        const n = new THREE.Vector3(nx, 1.2, nz).normalize();
        const t1 = new THREE.Vector3(1, 0, 0).cross(n).normalize(), t2 = n.clone().cross(t1).normalize();
        let k = 0;
        add(90, 20, 1.8, 1.3, 1.5, 0.3, {}, () => { const ang = (k++ / 90) * Math.PI * 2; return [t1.x * Math.cos(ang) + t2.x * Math.sin(ang), t1.y * Math.cos(ang) + t2.y * Math.sin(ang), t1.z * Math.cos(ang) + t2.z * Math.sin(ang)]; });
        break;
      }
      case 'crossette': add(14, 14, 1.9, 1.8, 0.9, 0.35, { split: 0.75, trail: 0.04 }); break;
      case 'palm': add(9, 16, 2.6, 2.2, 0.8, 0.6, { trail: 0.03 }, () => { const t = Math.random() * Math.PI * 2; const up = 0.35 + Math.random() * 0.4; const s = Math.sqrt(1 - up * up); return [s * Math.cos(t), up, s * Math.sin(t)]; }); break;
      case 'strobe': add(110, 15, 2.4, 1.2, 1.4, 0.3, { strobe: true, r: 1, g: 1, b: 1 }); break;
    }
    // THE SHELL SMOKE: 4–7 puffs at the break that drift with the wind and linger (lit by the NEXT breaks).
    const nP = 4 + Math.floor(Math.random() * 4);
    for (let i = 0; i < nP && this.puffs.length < SMOKE_CAP; i++) {
      const [dx, dy, dz] = sphere();
      this.puffs.push({ x: x + dx * 3 * scale, y: y + dy * 3 * scale, z: z + dz * 3 * scale, vx: dx * 1.4, vy: dy * 0.6 + 0.25, vz: dz * 1.4, age: 0, life: 10 + Math.random() * 6, size: (9 + Math.random() * 6) * scale, a: 0.5 + Math.random() * 0.2 });
      this.stats.puffs++;
    }
    // CRACKLE MICRO-STARS: a glitter over the ~2 s the crackle clip plays (seen now, heard at d / 343 m/s).
    if (kind === 'strobe' || kind === 'crossette' || kind === 'willow') {
      for (let i = 0; i < 70; i++) {
        const [dx, dy, dz] = sphere();
        const v = (11 + Math.random() * 5) * scale;
        this.push({ x, y, z, vx: dx * v, vy: dy * v, vz: dz * v, age: 0, life: 2.4, size: 0.55, drag: 1.3, grav: 0.3, r: 1, g: 0.97, b: 0.9, strobe: false, trail: 0, trailT: 0, split: 0, kind: 'peony', palette, burstH: 1, waterY, flashAt: 0.4 + Math.random() * 1.9 });
        this.stats.microStars++;
      }
    }
    // The flash lights the ground under the break (a broker FLASH at its long range); the report arrives at the speed of sound.
    const d = Math.hypot(x - atlas.localX, z - atlas.localZ, y - atlas.localY);
    const viewer = { x: atlas.localX, y: atlas.localY, z: atlas.localZ };
    if (fireSystem()?.flash({ x, y, z, color: a.getHex(), intensity: 9000 * scale, rangeM: 220, maxRangeM: 240, ms: 450,
      onCurve: { candela: FIREWORK_BREAK_CANDELA * scale, nits: EMITTER_NITS.spark, displayMax: EMITTER_DISPLAY_MAX.spark } }, viewer)) this.stats.flashes++; // surfaces (2026-09-27): §4.3
    if (this.flashLeft <= 0.2 || d < this.flashAt.distanceTo(new THREE.Vector3(atlas.localX, atlas.localY, atlas.localZ))) {
      this.flashAt.set(x, y, z);
      this.flashColor.copy(a);
      this.flashPeak = 3.2 * scale;
      this.flashLeft = 0.6;
    }
    if (d < 2600) {
      const key = sfxAvailable('sfx_firework_burst') ? 'sfx_firework_burst' : 'sfx_bolt_explosion';
      sfxAt(key, { x, y, z }, { gain: Math.min(0.55, 0.9 * (1 - d / 2600)) * (key === 'sfx_bolt_explosion' ? 0.5 : 1), rate: key === 'sfx_bolt_explosion' ? 0.55 + Math.random() * 0.1 : 0.9 + Math.random() * 0.2, delayS: d / SPEED_OF_SOUND, cooldownMs: 30, occlude: false });
      if (kind === 'strobe' || kind === 'crossette' || kind === 'willow') {
        const ck = sfxAvailable('sfx_firework_crackle') ? 'sfx_firework_crackle' : '';
        if (ck) sfxAt(ck, { x, y, z }, { gain: 0.3 * (1 - d / 2600), delayS: d / SPEED_OF_SOUND + 0.4, cooldownMs: 60, occlude: false });
      }
    }
  }

  private step(dt: number): void {
    const sp = this.sparks;
    for (let i = sp.length - 1; i >= 0; i--) {
      const s = sp[i];
      s.age += dt;
      if (s.age >= s.life) {
        if (s.kind === 'rocket') this.burst(s.x, s.y, s.z, s.shell ?? 'peony', s.palette, s.burstH);
        sp[i] = sp[sp.length - 1]; sp.pop();
        continue;
      }
      const drag = Math.exp(-s.drag * dt);
      s.vx *= drag; s.vy = s.vy * drag - GRAVITY * s.grav * dt; s.vz *= drag;
      s.x += s.vx * dt; s.y += s.vy * dt; s.z += s.vz * dt;
      if (s.trail > 0) {
        s.trailT += dt;
        while (s.trailT >= s.trail) {
          s.trailT -= s.trail;
          this.push({ x: s.x, y: s.y, z: s.z, vx: s.vx * 0.05, vy: s.vy * 0.05, vz: s.vz * 0.05, age: 0, life: 0.55, size: s.size * 0.55, drag: 2, grav: 0.2, r: s.r, g: s.g * 0.85, b: s.b * 0.6, strobe: false, trail: 0, trailT: 0, split: 0, kind: 'trail', palette: s.palette, burstH: 1 });
        }
      }
      if (s.split > 0 && s.age >= s.split) {
        s.split = 0;
        for (let k = 0; k < 4; k++) {
          const ang = (k / 4) * Math.PI * 2 + Math.random();
          this.push({ x: s.x, y: s.y, z: s.z, vx: s.vx * 0.3 + Math.cos(ang) * 9, vy: s.vy * 0.3 + (Math.random() - 0.3) * 5, vz: s.vz * 0.3 + Math.sin(ang) * 9, age: 0, life: 0.9, size: 1.1, drag: 1.2, grav: 0.4, r: s.r, g: s.g, b: s.b, strobe: false, trail: 0, trailT: 0, split: 0, kind: 'peony', palette: s.palette, burstH: 1 });
        }
      }
    }
  }

  /** Drift the shell smoke with the wind (spreading, slowing, rising a little) and light it by the latest flash. */
  private stepSmoke(dt: number): void {
    const wx = windVector.x * (1.5 + 3 * windVector.z), wz = windVector.y * (1.5 + 3 * windVector.z);
    for (let i = this.puffs.length - 1; i >= 0; i--) {
      const q = this.puffs[i];
      q.age += dt;
      if (q.age >= q.life) { this.puffs[i] = this.puffs[this.puffs.length - 1]; this.puffs.pop(); continue; }
      const k = Math.exp(-0.25 * dt);
      q.vx = q.vx * k + wx * (1 - k); q.vz = q.vz * k + wz * (1 - k); q.vy *= k;
      q.x += q.vx * dt; q.y += q.vy * dt; q.z += q.vz * dt;
    }
    const sm = this.smoke;
    if (!sm) return;
    const f = this.flashLeft > 0 ? this.flashPeak * (this.flashLeft / 0.6) ** 2 : 0;
    (sm.uFlashCol.value as THREE.Vector3).set(this.flashColor.r * f, this.flashColor.g * f, this.flashColor.b * f);
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    const amb = 0.02 + 0.5 * (1 - night);
    (sm.uAmbient.value as THREE.Vector3).set(amb, amb * 1.04, amb * 1.12);
    const ax = atlas.localX, ay = atlas.localY + 60, az = atlas.localZ;
    sm.sprite.position.set(ax, ay, az);
    sm.write(this.puffs, ax, ay, az, this.flashAt.x, this.flashAt.y, this.flashAt.z);
  }

  private animateBeams(now: number): void {
    for (const [k, show] of this.shows) {
      if (now > show.ends) {
        for (const b of show.beams) { this.scene.remove(b); (b.material as THREE.Material).dispose(); }
        this.shows.delete(k);
        continue;
      }
      const left = show.ends - now;
      const fade = Math.min(1, left / 3);
      for (const b of show.beams) {
        const ph = b.userData.phase as number, sp = b.userData.speed as number;
        b.rotation.z = Math.sin(now * sp + ph) * 0.45;
        b.rotation.x = Math.cos(now * sp * 0.8 + ph * 1.3) * 0.35;
        (b.material as THREE.MeshBasicMaterial).opacity = 0.55 * fade;
      }
    }
  }

  // ── Town night shows and festival shows ────────────────────────────────────
  private townShows(now: number): void {
    if (now - this.townShowAt < 5) return;
    this.townShowAt = now;
    if (atlas.currentWorldId !== atlas.commonsWorldId) return;
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    if (night < 0.6) return;
    const seed = commonsSeedNow();
    if (!seed) return;
    if (this.sitesSeed !== seed) { this.sitesSeed = seed; this.bigSites = settlementSites(seed).filter((x) => x.tier === 'city' || x.tier === 'capital'); }
    const tS = Date.now() / 1000;
    for (const s of this.bigSites) {
      if (Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ) > VIEW_M) continue;
      const off = hash32(`show:${seed}:${s.id}`) % SHOW_PERIOD_S;
      const phase = ((tS - off) % SHOW_PERIOD_S + SHOW_PERIOD_S) % SHOW_PERIOD_S;
      if (phase > TOWN_SHOW_S) continue;
      const slot = Math.floor((tS - off) / SHOW_PERIOD_S);
      const key = `town:${s.id}:${slot}`;
      if (this.shows.has(key)) continue;
      this.stats.townShows++;
      // Joining mid-show: only the remaining seconds (every client agrees on the choreography by the slot seed).
      this.startShow(key, s.centerX, s.centerZ, hash32(key), Math.max(8, TOWN_SHOW_S - phase), true);
    }
  }

  private festivalShows(now: number): void {
    const t = (atlas.conn?.db as unknown as { livingEvent?: { iter(): Iterable<{ eventId: bigint; worldId: bigint; kind: string; family: string; x: number; z: number; phase: string }> } } | undefined)?.livingEvent;
    if (!t) return;
    const night = (uNightFactor as unknown as { value: number }).value ?? 0;
    for (const e of t.iter()) {
      if (e.worldId !== atlas.currentWorldId || e.phase !== 'live') continue;
      const festive = e.kind === 'festival' || e.kind === 'parade' || (e.kind === 'feast' && night > 0.5);
      if (!festive) continue;
      if (Math.hypot(e.x - atlas.localX, e.z - atlas.localZ) > VIEW_M) continue;
      const k = e.eventId.toString();
      const last = this.festivalAt.get(k) ?? 0;
      if (now - last < FESTIVAL_EVERY_S) continue;
      this.festivalAt.set(k, now);
      this.stats.festivalShows++;
      const slot = Math.floor(Date.now() / 1000 / FESTIVAL_EVERY_S);
      this.startShow(`fest:${k}:${slot}`, e.x, e.z, hash32(`fest:${k}:${slot}`), 16, night > 0.4);
    }
    if (this.festivalAt.size > 64) this.festivalAt.clear();
  }

  private clearAll(): void {
    this.sparks.length = 0;
    this.launches.length = 0;
    for (const show of this.shows.values()) for (const b of show.beams) { this.scene.remove(b); (b.material as THREE.Material).dispose(); }
    this.shows.clear();
    this.batch?.write(this.sparks, 0, 0, 0, 0);
    this.mirror?.write([], 0, 0, 0, 0);
    this.puffs.length = 0;
    this.smoke?.write(this.puffs, 0, 0, 0, 0, 0, 0);
    this.flashLeft = 0;
  }
}
